import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, Clock, Calendar, PlayCircle, Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../lib/api';

/**
 * TMDB detail page for a movie or TV show. Route: `/tmdb/:media/:id`
 * Renders backdrop, poster, cast, genres, trailer (YouTube embed) and similar items.
 */
export default function TmdbDetail() {
  const { media, id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTrailer, setShowTrailer] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError('');
    api.get(`/tmdb/details/${media}/${id}`)
      .then(({ data }) => { if (!cancel) setItem(data); })
      .catch((e) => { if (!cancel) setError(e?.response?.data?.detail || 'Contenuto non disponibile'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [media, id]);

  const runtime = item?.runtime_minutes
    ? `${Math.floor(item.runtime_minutes / 60)}h ${item.runtime_minutes % 60}min`
    : null;

  return (
    <Layout title="Dettaglio">
      <div className="pb-20">
        {loading && (
          <div className="flex items-center justify-center py-20 text-white/50 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Caricamento…</span>
          </div>
        )}
        {!loading && error && (
          <div className="px-6 py-10 text-center">
            <div className="text-white/60 text-sm">{error}</div>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 rounded-full bg-[var(--gold)] text-black text-xs font-bold"
              data-testid="detail-back-btn"
            >
              Torna indietro
            </button>
          </div>
        )}
        {!loading && !error && item && (
          <div data-testid="tmdb-detail">
            {/* Backdrop hero */}
            <div className="relative w-full h-[45vh] sm:h-[55vh] overflow-hidden">
              {item.backdrop && (
                <img
                  src={item.backdrop}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[var(--bg-void)]" />
              <button
                onClick={() => navigate(-1)}
                className="absolute top-3 left-3 z-10 w-9 h-9 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center text-white/90 hover:text-[var(--gold)] transition-colors"
                data-testid="detail-back-hero"
                aria-label="Indietro"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="absolute inset-x-0 bottom-0 p-5 flex items-end gap-4">
                {item.poster && (
                  <img
                    src={item.poster}
                    alt={item.title}
                    className="w-24 h-36 sm:w-32 sm:h-48 rounded-lg shadow-2xl border border-white/10 object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <motion.h1
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-heading text-2xl sm:text-3xl leading-tight"
                    data-testid="detail-title"
                  >{item.title}</motion.h1>
                  {item.original_title && item.original_title !== item.title && (
                    <div className="text-xs text-white/50 italic mt-1">"{item.original_title}"</div>
                  )}
                  <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/70">
                    {item.year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{item.year}</span>}
                    {runtime && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{runtime}</span>}
                    {item.num_seasons != null && <span>{item.num_seasons} {item.num_seasons === 1 ? 'stagione' : 'stagioni'}</span>}
                    {item.status && item.media_type === 'tv' && <span className="text-white/50">· {translateStatus(item.status)}</span>}
                    {item.vote > 0 && (
                      <span className="flex items-center gap-1 text-[var(--gold)]">
                        <Star className="w-3 h-3 fill-[var(--gold)]" />{item.vote.toFixed ? item.vote.toFixed(1) : item.vote}
                        <span className="text-white/40">({item.vote_count})</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 mt-5 max-w-2xl mx-auto space-y-6">
              {/* Trailer CTA */}
              {item.trailer && !showTrailer && (
                <button
                  onClick={() => setShowTrailer(true)}
                  className="w-full h-11 rounded-full bg-[var(--gold)] text-black font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98]"
                  data-testid="detail-play-trailer"
                >
                  <PlayCircle className="w-5 h-5" />
                  Guarda il trailer
                </button>
              )}
              {item.trailer && showTrailer && (
                <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-white/10">
                  <iframe
                    title="Trailer"
                    src={`https://www.youtube.com/embed/${item.trailer.key}?autoplay=1`}
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    data-testid="detail-trailer-iframe"
                  />
                </div>
              )}

              {/* Genres */}
              {item.genres?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {item.genres.map((g) => (
                    <span key={g} className="text-[10px] uppercase tracking-widest text-[var(--gold)] border border-[var(--gold)]/30 rounded-full px-2.5 py-1">{g}</span>
                  ))}
                </div>
              )}

              {/* Tagline + Overview */}
              {item.tagline && <div className="text-sm italic text-white/60">"{item.tagline}"</div>}
              {item.overview && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Trama</div>
                  <p className="text-sm text-white/85 leading-relaxed whitespace-pre-line">{item.overview}</p>
                </div>
              )}

              {/* Meta info */}
              {item.director && (
                <div className="text-xs">
                  <span className="text-white/40">Regia: </span>
                  <span className="text-white/85">{item.director}</span>
                </div>
              )}

              {/* Cast */}
              {item.cast?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Cast principale</div>
                  <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                    {item.cast.map((c, i) => (
                      <div key={i} className="flex-shrink-0 w-20 text-center">
                        <div className="w-20 h-20 rounded-full overflow-hidden border border-white/10 bg-white/5">
                          {c.profile ? (
                            <img src={c.profile} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] text-white/30">?</div>
                          )}
                        </div>
                        <div className="mt-1.5 text-[10px] font-medium leading-tight line-clamp-2">{c.name}</div>
                        <div className="text-[9px] text-white/40 leading-tight line-clamp-2 mt-0.5">{c.character}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function translateStatus(s) {
  const map = {
    'Returning Series': 'In corso',
    'Ended': 'Conclusa',
    'Canceled': 'Cancellata',
    'In Production': 'In produzione',
    'Planned': 'Pianificata',
    'Pilot': 'Pilota',
  };
  return map[s] || s;
}
